import type { UnitStatReportViewModel } from 'src/api/types/UnitStatReportViewModel';
import type { UnitStatExposureDraftDelta } from '../exposures/unit-stat-exposures-edit.helpers';
import type {
  StateLookupOption,
  UnitStatUsrLookups,
  CodeDescriptionLookupOption,
  UpdatePolicyUnitStatReportRequest,
} from 'src/api/services/policy.service';

import {
  formatDate,
  mapPlanTypeCodeToDescription,
  mapEstimatedAuditCodeToDescription,
  mapCorrectionTypeCodeToDescription,
} from 'src/sections/employer-data/shared/utils/policyUtils';

export type UnitStatHeaderDraft = {
  insuredName: string;
  insuredAddress: string;
  correctionTypeCode: string;
  replacementReportIndicator: boolean;
  exposureStateCode: number | '';
  stateEffectiveDate: string;
  riskNumber: string;
  coverageTypeCode: string;
  planTypeCode: string;
  estimatedAuditCode: string;
  lossesSubjectDeductibleCode: string;
  deductibleClaimAccident: string;
  deductibleAggregate: string;
  basisDeductibleCalculationCode: string;
};

export type UnitStatHeaderLookups = UnitStatUsrLookups & {
  states: StateLookupOption[];
};

export const createUnitStatHeaderDraft = (
  report: UnitStatReportViewModel | undefined
): UnitStatHeaderDraft => ({
  insuredName: report?.insuredName ?? '',
  insuredAddress: report?.insuredAddress ?? '',
  correctionTypeCode: report?.correctionTypeCode ?? '',
  replacementReportIndicator: report?.replacementReportIndicator ?? false,
  exposureStateCode: report?.exposureStateCode ?? '',
  // Reformatted to MM/DD/YYYY (matching read-mode's formatDate) since EditableDatePicker parses
  // with that format, not whatever raw date format the API returns.
  stateEffectiveDate: formatDate(report?.stateEffectiveDate),
  riskNumber: report?.riskNumber ?? '',
  coverageTypeCode: report?.coverageTypeCode ?? '',
  planTypeCode: report?.planTypeCode ?? '',
  estimatedAuditCode: report?.estimatedAuditCode ?? '',
  lossesSubjectDeductibleCode: report?.lossesSubjectDeductibleCode ?? '',
  deductibleClaimAccident:
    report?.deductibleClaimAccident === null || report?.deductibleClaimAccident === undefined
      ? ''
      : String(report.deductibleClaimAccident),
  deductibleAggregate:
    report?.deductibleAggregate === null || report?.deductibleAggregate === undefined
      ? ''
      : String(report.deductibleAggregate),
  basisDeductibleCalculationCode: report?.basisDeductibleCalculationCode ?? '',
});

const editableFields: Array<keyof UnitStatHeaderDraft> = [
  'insuredName',
  'insuredAddress',
  'correctionTypeCode',
  'replacementReportIndicator',
  'exposureStateCode',
  'stateEffectiveDate',
  'riskNumber',
  'coverageTypeCode',
  'planTypeCode',
  'estimatedAuditCode',
  'lossesSubjectDeductibleCode',
  'deductibleClaimAccident',
  'deductibleAggregate',
  'basisDeductibleCalculationCode',
];

// Changed-values-only representation of a draft, for cross-tab-switch persistence without duplicating unedited read data.
export type UnitStatHeaderDraftDelta = Partial<UnitStatHeaderDraft>;

// Per-correction-version cache, keyed by `${reportNumber}.${correctionSequenceNumber}` (matches the
// component's outer tab value), so switching between correction tabs doesn't lose in-progress edits.
export type UnitStatReportsDraftDelta = Record<
  string,
  UnitStatHeaderDraftDelta & { exposures?: UnitStatExposureDraftDelta }
>;

export const getUnitStatHeaderDraftDelta = (
  snapshot: UnitStatHeaderDraft,
  draft: UnitStatHeaderDraft
): UnitStatHeaderDraftDelta => {
  const delta: UnitStatHeaderDraftDelta = {};
  editableFields.forEach((field) => {
    if (draft[field] !== snapshot[field]) {
      delta[field] = draft[field] as never;
    }
  });

  return delta;
};

export const applyUnitStatHeaderDraftDelta = (
  snapshot: UnitStatHeaderDraft,
  delta: UnitStatHeaderDraftDelta | null | undefined
): UnitStatHeaderDraft => ({ ...snapshot, ...delta });

export const createUnitStatHeaderUpdateRequest = (
  report: UnitStatReportViewModel,
  draft: UnitStatHeaderDraft
): UpdatePolicyUnitStatReportRequest => {
  const initial = createUnitStatHeaderDraft(report);
  const request: UpdatePolicyUnitStatReportRequest = {
    unitStatReportId: report.unitStatReportId ?? 0,
  };

  editableFields.forEach((field) => {
    if (draft[field] === initial[field]) {
      return;
    }

    switch (field) {
      case 'exposureStateCode':
        request.exposureStateCode = draft[field] === '' ? null : draft[field];
        break;
      case 'stateEffectiveDate':
        request.stateEffectiveDate = draft[field] === '' ? null : draft[field];
        break;
      case 'deductibleClaimAccident':
        request.deductibleClaimAccident =
          draft[field] === '' ? null : Number(draft[field]);
        break;
      case 'deductibleAggregate':
        request.deductibleAggregate = draft[field] === '' ? null : Number(draft[field]);
        break;
      default:
        request[field] = draft[field] as never;
    }
  });

  return request;
};

const getDescription = (options: CodeDescriptionLookupOption[], code: string) =>
  options.find((option) => option.code === code)?.description ?? code;

export const getUnitStatHeaderChangedFieldSummaries = (
  report: UnitStatReportViewModel,
  draft: UnitStatHeaderDraft,
  lookups: UnitStatHeaderLookups
): string[] => {
  const initial = createUnitStatHeaderDraft(report);
  const summaries: string[] = [];
  const describe = (label: string, before: string, after: string) => {
    if (before !== after) {
      summaries.push(`${label} changed from ${before || '(blank)'} to ${after || '(blank)'}.`);
    }
  };

  describe('Insured name', initial.insuredName, draft.insuredName);
  describe('Address', initial.insuredAddress, draft.insuredAddress);
  describe(
    'Correction type',
    mapCorrectionTypeCodeToDescription(initial.correctionTypeCode) ||
      getDescription(lookups.correctionTypes, initial.correctionTypeCode),
    mapCorrectionTypeCodeToDescription(draft.correctionTypeCode) ||
      getDescription(lookups.correctionTypes, draft.correctionTypeCode)
  );
  describe(
    'Replacement indicator',
    initial.replacementReportIndicator ? 'Yes' : 'No',
    draft.replacementReportIndicator ? 'Yes' : 'No'
  );
  describe(
    'Exposure state',
    lookups.states.find((state) => state.stateCode === initial.exposureStateCode)?.stateName ?? '',
    lookups.states.find((state) => state.stateCode === draft.exposureStateCode)?.stateName ?? ''
  );
  describe('State effective date', initial.stateEffectiveDate, draft.stateEffectiveDate);
  describe('Risk ID', initial.riskNumber, draft.riskNumber);
  describe(
    'Type coverage',
    getDescription(lookups.coverageTypes, initial.coverageTypeCode),
    getDescription(lookups.coverageTypes, draft.coverageTypeCode)
  );
  describe(
    'Market',
    mapPlanTypeCodeToDescription(initial.planTypeCode),
    mapPlanTypeCodeToDescription(draft.planTypeCode)
  );
  describe(
    'Audit complete',
    mapEstimatedAuditCodeToDescription(initial.estimatedAuditCode),
    mapEstimatedAuditCodeToDescription(draft.estimatedAuditCode)
  );
  describe(
    'Losses subject to deductible',
    getDescription(lookups.lossesSubjectDeductibleTypes, initial.lossesSubjectDeductibleCode),
    getDescription(lookups.lossesSubjectDeductibleTypes, draft.lossesSubjectDeductibleCode)
  );
  describe('Deductible amount - Claim / Accident', initial.deductibleClaimAccident, draft.deductibleClaimAccident);
  describe('Deductible amount - Aggregate', initial.deductibleAggregate, draft.deductibleAggregate);
  describe(
    'Basis of deductible calculation',
    getDescription(lookups.basisDeductibleCalculationTypes, initial.basisDeductibleCalculationCode),
    getDescription(lookups.basisDeductibleCalculationTypes, draft.basisDeductibleCalculationCode)
  );

  return summaries;
};

export type UnitStatHeaderFieldErrors = Partial<Record<keyof UnitStatHeaderDraft, string>>;

export const validateUnitStatHeaderDraft = (
  draft: UnitStatHeaderDraft,
  correctionSequenceNumber?: number,
  snapshot?: UnitStatHeaderDraft
): UnitStatHeaderFieldErrors => {
  const errors: UnitStatHeaderFieldErrors = {};
  // A field that hasn't been edited this session is acceptable as-is, even if it's missing or
  // invalid from before this refactor's edit UI existed; only validate fields the user touched.
  const isUnedited = (field: keyof UnitStatHeaderDraft) =>
    snapshot !== undefined && draft[field] === snapshot[field];

  const validateText = (field: 'insuredName' | 'insuredAddress', label: string) => {
    if (isUnedited(field)) return;
    if (!draft[field]) errors[field] = `${label} is required.`;
    else if (draft[field].length > 79 || !/^[\x20-\x7E]+$/.test(draft[field])) {
      errors[field] = `${label} must be 79 characters or fewer and contain only standard ASCII characters.`;
    }
  };

  validateText('insuredName', "Insured's name");
  validateText('insuredAddress', 'Address');

  if (!isUnedited('stateEffectiveDate') && draft.stateEffectiveDate && !/^\d{2}\/\d{2}\/\d{4}$/.test(draft.stateEffectiveDate)) {
    errors.stateEffectiveDate = 'State effective date must use MM/DD/YYYY format.';
  }
  if (!isUnedited('riskNumber') && draft.riskNumber && !/^\d{1,9}$/.test(draft.riskNumber)) {
    errors.riskNumber = 'Risk ID must contain no more than 9 numeric characters.';
  }
  if (!isUnedited('deductibleClaimAccident') && draft.deductibleClaimAccident && !/^\d{1,9}$/.test(draft.deductibleClaimAccident)) {
    errors.deductibleClaimAccident = 'Deductible amount must be a whole number with no more than 9 digits.';
  }
  if (!isUnedited('deductibleAggregate') && draft.deductibleAggregate && !/^\d{1,9}$/.test(draft.deductibleAggregate)) {
    errors.deductibleAggregate = 'Deductible amount must be a whole number with no more than 9 digits.';
  }
  if (
    correctionSequenceNumber === 0 &&
    !isUnedited('correctionTypeCode') &&
    draft.correctionTypeCode === 'L'
  ) {
    errors.correctionTypeCode = 'Loss correction type is not valid for an x.0 report.';
  }
  return errors;
};
